// src/utils/uploadFile.js
// The ONE browser-side way to upload a file after the R2 cutover. Replaces every direct browser-side
// storage upload call — R2 has no anon client, so the browser
// gets a presigned PUT from a staff-gated endpoint and PUTs the bytes DIRECTLY to R2 (never through a
// Vercel function → dodges the 4.5 MB body cap).
//
// ⛔ Returns supabase's SHAPE — `{ error: null }` on success, `{ error: Error }` on failure — so each call
// site is a drop-in swap (`supabase.storage…upload(path, file)` → `uploadFile(path, file)`) and every
// site's existing error handling stays intact. Flow: sign → PUT → record (logs upload after success).
import { supabase } from '../supabase';

export async function uploadFile(storagePath, file) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session && session.access_token;
    if (!token) return { error: new Error('uploadFile: not signed in') };
    const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
    const contentType = file.type || 'application/octet-stream';

    // 1. sign — get a presigned PUT (ContentType is signed, so we MUST PUT with the same value below)
    const signResp = await fetch('/api/staff-upload-sign', {
      method: 'POST', headers: authHeaders,
      body: JSON.stringify({ storage_path: storagePath, content_type: contentType }),
    });
    if (!signResp.ok) {
      let reason = String(signResp.status);
      try { const j = await signResp.json(); reason = j.error || reason; } catch { /* non-json */ }
      return { error: new Error(`uploadFile sign: ${reason}`) };
    }
    const { url } = await signResp.json();
    if (!url) return { error: new Error('uploadFile: no sign url') };

    // 2. PUT the bytes straight to R2 (Content-Type must match what was signed)
    const putResp = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
    if (!putResp.ok) return { error: new Error(`uploadFile PUT: ${putResp.status}`) };

    // 3. ⛔ VERIFY-then-record — REQUIRED, not best-effort. staff-upload-record does a server-side HeadObject
    //    and only returns ok if the object is provably in R2 with real bytes. If it refuses, the upload did
    //    NOT land (a "200" PUT that stored nothing, a wrong bucket, etc.) — we MUST return an error so the
    //    caller does NOT create a file_exchange row for a key with no bytes. This is the data-loss guard.
    let recResp;
    try {
      recResp = await fetch('/api/staff-upload-record', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ storage_path: storagePath }),
      });
    } catch (e) { return { error: new Error(`uploadFile verify: ${e.message}`) }; }
    if (!recResp.ok) {
      let reason = String(recResp.status);
      try { const j = await recResp.json(); reason = j.error || reason; } catch { /* non-json */ }
      return { error: new Error(`uploadFile not verified in storage: ${reason}`) };
    }

    return { error: null };
  } catch (e) {
    return { error: e };
  }
}
